import React, { useState, useEffect, useContext, useCallback } from "react";
import {
  Typography,
  MenuItem,
  Box,
  Button,
  IconButton,
  Tooltip,
  Menu,
  Dialog,
  Divider,
} from "@mui/material";
import DrawIcon from "@mui/icons-material/Draw";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DownloadIcon from "@mui/icons-material/Download";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import CompareIcon from "@mui/icons-material/Compare";
import TuneIcon from "@mui/icons-material/Tune";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { alpha } from "@mui/material/styles";
import {
  storeCurrentPdf,
  listStoredPdfs,
  compareWithStoredById,
} from "../../../../utils/pdf/comparePdf";
import { AuthContext } from "../../../../contexts/AuthContext";
import { setSubscriptionStatus } from "../../../../utils/pdf/subscriptionStatus";
import DialogLoading from "../../../App/DialogLoading";
import OptionSwitch from "../../../utils/button/OptionSwitch";
import PdfSettingsDialog, { CUSTOM_DENSITY_VALUE } from "./PdfSettingsDialog";
import {
  getCachedUserPreference,
  getUserPreference,
  subscribeUserPreferenceChange,
  updatePdfPreference,
} from "../../../../contexts/services/userPreferenceServices";

const waitForNextPaint = () =>
  new Promise((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(resolve));
  });

const isSameJsonValue = (left, right) =>
  JSON.stringify(left) === JSON.stringify(right);

const PdfBase = ({
  generatePdf,
  downloadName,
  enableDraftView = false,
  pdfDensityOptions,
  pdfPreferenceKey = "settings",
  pdfSettingsContent,
  pdfSpacingOptions,
  pdfSpacingPresets,
  pdfTypeOptions,
}) => {
  const { global_authState } = useContext(AuthContext);
  const cachedPdfPreference =
    getCachedUserPreference()?.pdf?.[pdfPreferenceKey] || {};
  const hasPdfTypeOptions =
    Array.isArray(pdfTypeOptions?.options) && pdfTypeOptions.options.length > 1;
  const [internalPdfType, setInternalPdfType] = useState(
    pdfTypeOptions?.defaultValue ?? pdfTypeOptions?.options?.[0]?.value
  );
  const selectedPdfTypeOption =
    pdfTypeOptions?.value ??
    internalPdfType ??
    pdfTypeOptions?.options?.[0]?.value;
  const hasPdfDensityOptions =
    Array.isArray(pdfDensityOptions?.options) &&
    pdfDensityOptions.options.length > 1;
  const [internalPdfDensity, setInternalPdfDensity] = useState(
    cachedPdfPreference.pdfDensity ??
      pdfDensityOptions?.defaultValue ??
      pdfDensityOptions?.options?.[0]?.value
  );
  const selectedPdfDensityOption =
    pdfDensityOptions?.value ??
    internalPdfDensity ??
    pdfDensityOptions?.options?.[0]?.value;

  const isManagedPdfSpacingOptions =
    pdfSpacingOptions &&
    (Object.prototype.hasOwnProperty.call(pdfSpacingOptions, "value") ||
      Object.prototype.hasOwnProperty.call(pdfSpacingOptions, "defaultValue") ||
      typeof pdfSpacingOptions?.onChange === "function");
  const [internalPdfSpacingOptions, setInternalPdfSpacingOptions] = useState(
    isManagedPdfSpacingOptions
      ? (pdfSpacingOptions?.defaultValue ?? pdfSpacingOptions?.value)
      : Object.prototype.hasOwnProperty.call(
            cachedPdfPreference,
            "pdfSpacingOptions"
          )
        ? cachedPdfPreference.pdfSpacingOptions
        : pdfSpacingOptions
  );
  const selectedPdfSpacingOptions = isManagedPdfSpacingOptions
    ? (pdfSpacingOptions?.value ?? internalPdfSpacingOptions)
    : internalPdfSpacingOptions;
  const hasPdfSpacingOptions =
    pdfSpacingOptions !== undefined && pdfSpacingOptions !== null;
  const hasPdfSettings =
    hasPdfDensityOptions || hasPdfSpacingOptions || Boolean(pdfSettingsContent);
  const hasSpacingSettings = hasPdfDensityOptions || hasPdfSpacingOptions;
  const selectedPdfSpacingLabel =
    pdfDensityOptions?.options?.find(
      (option) => option.value === selectedPdfDensityOption
    )?.label ??
    (selectedPdfDensityOption === CUSTOM_DENSITY_VALUE ? "Customize" : "Default");
  const generateOptions = {
    ...(hasPdfTypeOptions ? { pdfType: selectedPdfTypeOption } : {}),
    ...(hasPdfDensityOptions ? { pdfDensity: selectedPdfDensityOption } : {}),
    ...(selectedPdfDensityOption === "customize"
      ? { pdfSpacingOptions: selectedPdfSpacingOptions || {} }
      : {}),
  };
  const savePdfPreference = async (valuePatch) => {
    if (!hasPdfSettings || !pdfPreferenceKey) return;

    await updatePdfPreference({
      value: {
        [pdfPreferenceKey]: {
          pdfDensity: selectedPdfDensityOption,
          pdfSpacingOptions: selectedPdfSpacingOptions ?? null,
          ...valuePatch,
        },
      },
    });
  };
  const handlePdfTypeChange = (nextValue) => {
    setInternalPdfType(nextValue);
    pdfTypeOptions?.onChange?.(nextValue);
  };
  const handlePdfDensityChange = (nextValue) => {
    setInternalPdfDensity(nextValue);
    pdfDensityOptions?.onChange?.(nextValue);
  };
  const handlePdfSpacingOptionsChange = (nextValue) => {
    setInternalPdfSpacingOptions(nextValue);
    if (isManagedPdfSpacingOptions) pdfSpacingOptions?.onChange?.(nextValue);
  };
  const handlePdfSettingsSave = ({ pdfDensity, pdfSpacingOptions }) => {
    return savePdfPreference({
      pdfDensity,
      pdfSpacingOptions: pdfSpacingOptions ?? null,
    });
  };
  const [pdfSettingsOpen, setPdfSettingsOpen] = useState(false);
  const applyPdfPreference = useCallback(
    (preference) => {
      const pdfPreference = preference?.pdf?.[pdfPreferenceKey] || {};

      if (!pdfDensityOptions?.value && pdfPreference.pdfDensity) {
        setInternalPdfDensity((prev) =>
          prev === pdfPreference.pdfDensity ? prev : pdfPreference.pdfDensity
        );
      }
      if (
        !isManagedPdfSpacingOptions &&
        Object.prototype.hasOwnProperty.call(pdfPreference, "pdfSpacingOptions")
      ) {
        setInternalPdfSpacingOptions((prev) =>
          isSameJsonValue(prev, pdfPreference.pdfSpacingOptions)
            ? prev
            : pdfPreference.pdfSpacingOptions
        );
      }
    },
    [isManagedPdfSpacingOptions, pdfDensityOptions?.value, pdfPreferenceKey]
  );

  useEffect(() => {
    if (!hasPdfSettings) return undefined;

    let isMounted = true;
    const loadPreference = async () => {
      try {
        const preference = await getUserPreference();

        if (!isMounted) return;
        applyPdfPreference(preference);
      } catch {
        // Keep local PDF settings when remote preference loading fails.
      }
    };

    loadPreference();

    return () => {
      isMounted = false;
    };
  }, [applyPdfPreference, hasPdfSettings]);

  useEffect(() => {
    if (!hasPdfSettings) return undefined;

    return subscribeUserPreferenceChange((preference) => {
      applyPdfPreference(preference);
    });
  }, [applyPdfPreference, hasPdfSettings]);

  // Set subscription status before generating PDF
  useEffect(() => {
    const status = global_authState?.subscription?.status || null;
    setSubscriptionStatus(status);
  }, [global_authState?.subscription?.status]);

  const openPdfInNewTab = async (doc, title, pdfType) => {
    // Keep separate recent histories for draft and final PDFs.
    await storeCurrentPdf(doc, title, pdfType);

    doc.setProperties({
      title,
    });

    const pdfBlob = doc.output("blob");
    const pdfUrl = URL.createObjectURL(pdfBlob);

    const faviconUrl = `${window.location.origin}/favicon.ico`;

    const html = `
      <html>
        <head>
          <title>${title}</title>
          <link rel="icon" href="${faviconUrl}" />
          <style>
            html, body {
              margin: 0;
              padding: 0;
              height: 100%;
              overflow: hidden;
            }
            embed {
              width: 100%;
              height: 100%;
              border: none;
            }
          </style>
        </head>
        <body>
          <embed src="${pdfUrl}" type="application/pdf" />
        </body>
      </html>
    `;

    const htmlBlob = new Blob([html], { type: "text/html" });
    const htmlUrl = URL.createObjectURL(htmlBlob);

    window.open(htmlUrl, "_blank");
  };

  const viewDraft = async () => {
    const doc = await Promise.resolve(generatePdf(true, generateOptions));
    await openPdfInNewTab(doc, downloadName + " Draft", "draft");
  };

  const viewPdf = async () => {
    const doc = await Promise.resolve(generatePdf(false, generateOptions));
    await openPdfInNewTab(doc, "Final " + downloadName, "final");
  };

  const downloadPdf = async () => {
    const doc = await Promise.resolve(generatePdf(false, generateOptions));
    await storeCurrentPdf(doc, `Final ${downloadName}`, "final");

    const now = new Date();

    // Format date as YYYY-MM-DD
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");
    const formattedDate = `${year}-${month}-${day}`;

    // Format time as HHMM
    const hours = String(now.getHours()).padStart(2, "0");
    const minutes = String(now.getMinutes()).padStart(2, "0");
    const formattedTime = `${hours}${minutes}`;

    // Create a clean filename by removing any special characters
    const cleanName = downloadName.replace(/[^a-zA-Z0-9\s-]/g, "").trim();

    // Format: DocumentName_YYYY-MM-DD_HHMM.pdf
    const filename = `${cleanName}_${formattedDate}_${formattedTime}.pdf`;

    doc.save(filename);
  };

  // Compare dropdown state
  const [anchorEl, setAnchorEl] = useState(null);
  const [compareMode, setCompareMode] = useState(null); // 'draft' | 'final' | 'current'
  const [highlightDifferences, setHighlightDifferences] = useState(true);
  const [isGeneratingComparison, setIsGeneratingComparison] = useState(false);
  const compareMenuOpen = Boolean(anchorEl);
  const selectedPdfType = compareMode === "draft" ? "draft" : "final";
  const storedList = listStoredPdfs(selectedPdfType);

  const onOpenCompareMenu = (event, mode) => {
    event.stopPropagation();
    setCompareMode(mode);
    setAnchorEl(event.currentTarget);
  };

  const handleSelectCompare = async (item) => {
    setAnchorEl(null);
    if (!item) return;
    const shouldHighlightDifferences = highlightDifferences;

    if (shouldHighlightDifferences) {
      setIsGeneratingComparison(true);
      await waitForNextPaint();
    }

    try {
      const isDraft = selectedPdfType === "draft";
      const doc = await Promise.resolve(generatePdf(isDraft, generateOptions));
      const title = `${isDraft ? "Draft" : "Final"} ${downloadName}`;
      await compareWithStoredById(doc, item.id, {
        title,
        highlightDifferences: shouldHighlightDifferences,
        pdfType: selectedPdfType,
      });
    } finally {
      if (shouldHighlightDifferences) setIsGeneratingComparison(false);
    }
  };

  return (
    <>
      <Box
        sx={{
          px: 2,
          py: 1.1,
          borderBottom: 1,
          borderColor: "divider",
          mb: 1,
          alignItems: "center",
          display: "flex",
          gap: 1.5,
          justifyContent: "space-between",
        }}
      >
        <Typography
          variant='subtitle2'
          sx={{
            fontWeight: 600,
            color: "error.main",
            textTransform: "uppercase",
            letterSpacing: 0.5,
            fontSize: "0.75rem",
            display: "flex",
            alignItems: "center",
            gap: 1,
          }}
        >
          Pdf Options
        </Typography>
        {hasPdfSettings && (
          <Button
            size='small'
            variant='outlined'
            aria-haspopup='dialog'
            aria-label={
              hasSpacingSettings
                ? `Change PDF spacing: ${selectedPdfSpacingLabel}`
                : "Change PDF settings"
            }
            endIcon={<ChevronRightIcon />}
            onClick={(event) => {
              event.stopPropagation();
              setPdfSettingsOpen(true);
            }}
            sx={{
              minWidth: 0,
              flexShrink: 0,
              gap: 0.5,
              px: 0.75,
              py: 0.25,
              borderRadius: 1,
              fontSize: "0.7rem",
              lineHeight: 1.5,
              textTransform: "none",
              whiteSpace: "nowrap",
              borderColor: "divider",
              "& .MuiButton-endIcon": {
                ml: 0,
                mr: -0.25,
                "& > svg": { fontSize: 16 },
              },
              "&:hover": {
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
                borderColor: "primary.main",
              },
              "&.Mui-focusVisible": {
                outline: "2px solid",
                outlineColor: "primary.main",
                outlineOffset: 2,
              },
            }}
          >
            <Box component='span' sx={{ color: "text.secondary" }}>
              {hasSpacingSettings ? "Spacing:" : "Settings"}
            </Box>
            {hasSpacingSettings && (
              <Box component='span' sx={{ fontWeight: 600 }}>
                {selectedPdfSpacingLabel}
              </Box>
            )}
          </Button>
        )}
      </Box>
      {hasPdfTypeOptions && (
        <Box
          onClick={(event) => event.stopPropagation()}
          sx={{
            borderRadius: 1,
            display: "flex",
            flexDirection: "column",
            gap: 2,
            mx: 1,
            mb: 0.5,
            p: 2,
            "&:hover": {
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
            },
          }}
        >
          <Box
            sx={{
              alignItems: "center",
              display: "flex",
              gap: 2,
              width: "100%",
            }}
          >
            <Box
              sx={{
                alignItems: "center",
                bgcolor: (theme) => alpha(theme.palette.info.main, 0.1),
                borderRadius: 1,
                display: "flex",
                flexShrink: 0,
                height: 36,
                justifyContent: "center",
                width: 36,
              }}
            >
              <TuneIcon color='info' fontSize='small' />
            </Box>
            <Box sx={{ width: "240px" }}>
              <Typography variant='body2' sx={{ fontWeight: 500 }}>
                {pdfTypeOptions?.label || "PDF Format"}
              </Typography>
              <Typography variant='caption' color='text.secondary'>
                {pdfTypeOptions?.description ||
                  "Choose the layout used when generating this PDF."}
              </Typography>
            </Box>
          </Box>
          <Box
            sx={{
              alignItems: "center",
              display: "flex",
              width: "100%",
            }}
          >
            <OptionSwitch
              disabled={pdfTypeOptions?.disabled}
              options={pdfTypeOptions.options}
              value={selectedPdfTypeOption}
              onChange={handlePdfTypeChange}
              sx={{
                width: "100%",
                "& .MuiToggleButton-root": {
                  flex: 1,
                },
                ...pdfTypeOptions?.sx,
              }}
            />
          </Box>
        </Box>
      )}
      {enableDraftView ? (
        <>
          <MenuItem
            onClick={() => viewDraft(false)}
            sx={{
              borderRadius: 1,
              mx: 1,
              mb: 0.5,
              py: 1.5,
              "&:hover": {
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                width: "100%",
              }}
            >
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: 1,
                  bgcolor: (theme) => alpha(theme.palette.warning.main, 0.1),
                }}
              >
                <DrawIcon color='warning' fontSize='small' />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography variant='body2' sx={{ fontWeight: 500 }}>
                    View Draft
                  </Typography>
                  <Tooltip title='Compare with previous PDF'>
                    <IconButton
                      size='small'
                      color='primary'
                      onClick={(e) => onOpenCompareMenu(e, "draft")}
                    >
                      <CompareIcon fontSize='small' />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Typography variant='caption' color='text.secondary'>
                  Preview document in draft mode
                </Typography>
              </Box>
            </Box>
          </MenuItem>

          <MenuItem
            onClick={() => viewPdf(false)}
            sx={{
              borderRadius: 1,
              mx: 1,
              mb: 0.5,
              py: 1.5,
              "&:hover": {
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
              },
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                width: "100%",
              }}
            >
              <Box
                sx={{
                  width: 36,
                  height: 36,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  borderRadius: 1,
                  bgcolor: (theme) => alpha(theme.palette.error.main, 0.08),
                }}
              >
                <PictureAsPdfIcon color='error' fontSize='small' />
              </Box>
              <Box sx={{ flex: 1 }}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography variant='body2' sx={{ fontWeight: 500 }}>
                    View Final
                  </Typography>
                  <Tooltip title='Compare with previous PDF'>
                    <IconButton
                      size='small'
                      color='primary'
                      onClick={(e) => onOpenCompareMenu(e, "final")}
                    >
                      <CompareIcon fontSize='small' />
                    </IconButton>
                  </Tooltip>
                </Box>
                <Typography variant='caption' color='text.secondary'>
                  Preview document in final mode
                </Typography>
              </Box>
            </Box>
          </MenuItem>
        </>
      ) : (
        <MenuItem
          onClick={() => viewPdf(false)}
          sx={{
            borderRadius: 1,
            mx: 1,
            mb: 0.5,
            py: 1.5,
            "&:hover": {
              bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
            },
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              width: "100%",
            }}
          >
            <Box
              sx={{
                width: 36,
                height: 36,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                borderRadius: 1,
                bgcolor: (theme) => alpha(theme.palette.primary.main, 0.1),
              }}
            >
              <VisibilityIcon color='primary' fontSize='small' />
            </Box>
            <Box sx={{ flex: 1 }}>
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Typography variant='body2' sx={{ fontWeight: 500 }}>
                  View PDF
                </Typography>
                <Tooltip title='Compare with stored PDF'>
                  <IconButton
                    size='small'
                    color='primary'
                    onClick={(e) => onOpenCompareMenu(e, "current")}
                  >
                    <CompareIcon fontSize='small' />
                  </IconButton>
                </Tooltip>
              </Box>
              <Typography variant='caption' color='text.secondary'>
                Preview document in current mode
              </Typography>
            </Box>
          </Box>
        </MenuItem>
      )}
      <MenuItem
        onClick={downloadPdf}
        sx={{
          borderRadius: 1,
          mx: 1,
          mb: 1,
          py: 1.5,
          "&:hover": {
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.08),
          },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2,
            width: "100%",
          }}
        >
          <Box
            sx={{
              width: 36,
              height: 36,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              borderRadius: 1,
              bgcolor: (theme) => alpha(theme.palette.error.main, 0.1),
            }}
          >
            <DownloadIcon color='error' fontSize='small' />
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant='body2' sx={{ fontWeight: 500 }}>
              Download PDF
            </Typography>
            <Typography variant='caption' color='text.secondary'>
              Save document as PDF file
            </Typography>
          </Box>
        </Box>
      </MenuItem>
      <Menu
        open={compareMenuOpen}
        anchorEl={anchorEl}
        onClose={() => setAnchorEl(null)}
        anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
        transformOrigin={{ vertical: "top", horizontal: "right" }}
        PaperProps={{
          sx: {
            width: 280, // 稍微收窄宽度
            borderRadius: 2,
            boxShadow: 4,
            overflow: "visible",
            mt: 1,
          },
        }}
      >
        <Box sx={{ px: 2, pt: 1.5, pb: 1 }}>
          <Typography variant='subtitle2'>
            Compare previous {selectedPdfType} PDF
          </Typography>
          <Typography variant='caption' color='text.secondary'>
            Select from the 3 most recent {selectedPdfType} versions
          </Typography>
        </Box>
        <Box sx={{ m: 1, ml: 2 }}>
          <Box
            onClick={() => setHighlightDifferences(!highlightDifferences)}
            sx={{
              display: "inline-flex",
              alignItems: "center",
              gap: 1,
              cursor: "pointer",
              userSelect: "none",
              p: 0.5,
              borderRadius: 1,
              "&:hover": { bgcolor: "action.hover" },
            }}
          >
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: "50%",
                bgcolor: highlightDifferences ? "success.main" : "grey.400",
                transition: "all 0.2s ease",
                boxShadow: highlightDifferences
                  ? "0 0 4px rgba(76, 175, 80, 0.6)"
                  : "none",
                flexShrink: 0,
              }}
            />

            <Typography variant='body2' sx={{ fontWeight: 500 }}>
              Highlight differences {highlightDifferences ? "(On)" : "(Off)"}
            </Typography>
          </Box>
        </Box>
        <Divider />
        {storedList.length === 0 ? (
          <MenuItem disabled sx={{ opacity: 0.6, pl: 3, py: 1.5 }}>
            No stored {selectedPdfType} PDFs yet
          </MenuItem>
        ) : (
          storedList.map((item, idx) => (
            <MenuItem
              key={item.id}
              onClick={() => handleSelectCompare(item)}
              sx={{
                alignItems: "flex-start",
                mx: 1,
                my: 0.5,
                borderRadius: 1,
                py: 1,
              }}
            >
              <Box sx={{ flexGrow: 1, minWidth: 0 }}>
                <Typography variant='body2' noWrap sx={{ fontWeight: 500 }}>
                  {item.label || `Version ${storedList.length - idx}`}
                </Typography>
                <Typography variant='caption' color='text.secondary' noWrap>
                  {new Date(item.ts).toLocaleString(undefined, {
                    month: "short",
                    day: "numeric",
                    hour: "2-digit",
                    minute: "2-digit",
                  })}
                </Typography>
              </Box>
            </MenuItem>
          ))
        )}
      </Menu>

      <Dialog
        open={isGeneratingComparison}
        disableEscapeKeyDown
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: 4,
            overflow: "hidden",
            width: "100%",
          },
        }}
      >
        <DialogLoading
          title='Generating comparison...'
          content='Please wait while we prepare the PDF comparison.'
        />
      </Dialog>
      <PdfSettingsDialog
        open={pdfSettingsOpen}
        onClose={() => setPdfSettingsOpen(false)}
        densityOptions={pdfDensityOptions}
        onDensityChange={handlePdfDensityChange}
        onSave={handlePdfSettingsSave}
        onSpacingOptionsChange={handlePdfSpacingOptionsChange}
        pdfSettingsContent={pdfSettingsContent}
        selectedDensity={selectedPdfDensityOption}
        spacingOptions={selectedPdfSpacingOptions}
        spacingPresets={pdfSpacingPresets}
      />
    </>
  );
};

export default PdfBase;
